class CdiLoadMore extends HTMLElement {
  connectedCallback() {
    const gridId = this.dataset.gridId || 'product-grid';
    this.grid = document.getElementById(gridId);
    this._gridId = gridId;
    this._isSearch = this.hasAttribute('data-search-mode');

    this.btn = this.querySelector('[data-load-more]');
    this.topBtn = this.querySelector('[data-back-top]');
    this.bar = this.querySelector('[data-progress-fill]');
    this.dot = this.querySelector('[data-progress-dot]');
    this.status = this.querySelector('[data-status]');
    this.btnWrap = this.querySelector('.cdi-load-more__actions');

    this.currentPage = parseInt(this.dataset.currentPage, 10);
    this.totalPages = parseInt(this.dataset.totalPages, 10);
    this.totalProducts = parseInt(this.dataset.totalProducts, 10);
    this.perPage = parseInt(this.dataset.perPage, 10);
    this.sectionId = this.dataset.sectionId;
    this.loading = false;

    this._updateUI();

    if (this.btn) this.btn.addEventListener('click', () => this._load());
    if (this.topBtn) this.topBtn.addEventListener('click', () => {
      this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  async _load() {
    if (this.loading || this.currentPage >= this.totalPages) return;
    this.loading = true;
    this.btn.setAttribute('aria-busy', 'true');
    this.btn.classList.add('cdi-load-more__btn--loading');

    const nextPage = this.currentPage + 1;
    const expected = Math.min(this.perPage, this.totalProducts - this.currentPage * this.perPage);
    const skeletons = this._insertSkeletons(expected);
    this._updateUI(nextPage);

    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage);
    url.searchParams.set('section_id', this.sectionId);
    if (this._isSearch) url.searchParams.set('type', 'product');

    try {
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = doc.querySelectorAll('#' + this._gridId + ' > li');

      items.forEach((li, i) => {
        li.classList.remove('scroll-trigger', 'animate--slide-in');
        if (i < skeletons.length) {
          this.grid.replaceChild(li, skeletons[i]);
        } else {
          this.grid.appendChild(li);
        }
      });
      for (let i = items.length; i < skeletons.length; i++) {
        skeletons[i].remove();
      }

      if (items.length) {
        const firstNew = this.grid.querySelector('li:nth-last-child(' + items.length + ')');
        if (firstNew) firstNew.querySelector('a, button, [tabindex]')?.focus({ preventScroll: true });
      }

      this.currentPage = nextPage;
      this.dataset.currentPage = nextPage;

      document.dispatchEvent(new CustomEvent('cdi:products-loaded', { bubbles: false }));

      const canonical = new URL(window.location.href);
      canonical.searchParams.delete('page');
      canonical.searchParams.delete('section_id');
      history.replaceState(null, '', canonical.toString());

      this._updateUI();
    } catch (e) {
      skeletons.forEach((s) => s.remove());
      this._updateUI();
      console.error('[cdi-load-more]', e);
    } finally {
      this.loading = false;
      this.btn.setAttribute('aria-busy', 'false');
      this.btn.classList.remove('cdi-load-more__btn--loading');
    }
  }

  _insertSkeletons(count) {
    const out = [];
    if (!this.grid || count < 1) return out;
    const ref = this.grid.querySelector('li:last-child');
    const h = ref ? Math.round(ref.getBoundingClientRect().height) : 0;
    for (let i = 0; i < count; i++) {
      const li = document.createElement('li');
      li.className = 'grid__item cdi-load-more__skeleton';
      li.setAttribute('aria-hidden', 'true');
      if (h) li.style.minHeight = h + 'px';
      const pulse = document.createElement('div');
      pulse.className = 'cdi-load-more__skeleton-pulse';
      li.appendChild(pulse);
      this.grid.appendChild(li);
      out.push(li);
    }
    return out;
  }

  _updateUI(page) {
    const p = page === undefined ? this.currentPage : page;
    const loaded = Math.min(p * this.perPage, this.totalProducts);
    const pct = (loaded / this.totalProducts) * 100;
    const done = p >= this.totalPages;

    if (this.bar) this.bar.style.width = pct + '%';
    if (this.dot) this.dot.style.left = pct + '%';
    if (this.status) {
      this.status.textContent =
        (this.status.dataset.tplBefore || 'Showing ') +
        loaded +
        (this.status.dataset.tplMid || ' of ') +
        this.totalProducts +
        (this.status.dataset.tplAfter || ' results');
    }
    if (this.btnWrap) this.btnWrap.classList.toggle('cdi-load-more__actions--hidden', done);
  }
}

if (!customElements.get('cdi-load-more')) {
  customElements.define('cdi-load-more', CdiLoadMore);
}
